import { createContext, useContext, useEffect, useState } from 'react';
import { userAxios } from '../utils/api';
import { useAuth } from './AuthContext';
import { handlePrintModal, logger } from '../utils/SiteHelpers';
import { LoaderContext } from './LoaderContext';
import { capitalize } from '../utils/Utils';

const CourseContext = createContext();

export const CourseProvider = ({ children }) => {
    const [courses, setCourses] = useState([]);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { accessToken, userId, user } = useAuth();
    const [emailedCertificates, setEmailedCertificates] = useState([]);
    const [lesson, setLesson] = useState({});
    const { setShowLoader, setLoaderMessage } = useContext(LoaderContext);
    const fetchCourses = async () => {
        try {
            if (accessToken) {
                const customHeaders = {
                    Authorization: `Bearer ${accessToken}`,
                };
                const res = await userAxios.get(`cle/courses`, {
                    headers: customHeaders,
                });
                const basicCourses = res?.data?.data?.data || [];
                setCourses(basicCourses);
            }
        } catch (err) {
            setError(err.message || 'Failed to load courses');
        } finally {
            setLoading(false);
        }
    };

    const getCourseTopics = async (courseId) => {
        const courseIndex = courses.findIndex((c) => c.id === courseId);
        if (courseIndex === -1 || courses[courseIndex]?.lessons) return;

        try {
            const customHeaders = {
                Authorization: `Bearer ${accessToken}`,
            };
            const res = await userAxios.get(`cle/courses/${courseId}/topic`, {
                headers: customHeaders,
            });
            const topic = res?.data?.data?.data || [];

            setTopics(topic);
        } catch (err) {
            console.error('Failed to fetch course topics', err);
        }
    };

    const getLesson = async (lessonId) => {
        const courseIndex = topics.findIndex((t) => t.id === lessonId);

        try {
            const customHeaders = {
                Authorization: `Bearer ${accessToken}`,
            };
            const res = await userAxios.get(`cle/topic/${lessonId}`, {
                headers: customHeaders,
            });
            const singleTopic = res?.data?.data || {};

            setLesson(singleTopic);
        } catch (err) {
            console.error('Failed to fetch course topics', err);
        }
    };
    const emailCertificate = async (courseId) => {
        setShowLoader(true);
        setLoaderMessage('Generating and Emailing Certificate');
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const payload = {
                user_id: userId,
                course_id: courseId,
            };
            await userAxios.post(`/cle/email-certificate`, payload, {
                headers: headers,
            });
            return { success: true };
        } catch (err) {
            console.error('Failed to email certificate:', err);
            return { error: err.message || 'Error emailing certificate' };
        } finally {
            setShowLoader(false);
        }
    };
    const downloadCertificate = async (
        courseId,
        launchPdfInModal = false,
        isPrint = false,
    ) => {
        setShowLoader(true);
        setLoaderMessage(
            launchPdfInModal
                ? 'Generating and Launching Certificate'
                : isPrint
                ? 'Generating and Printing Certificate'
                : 'Generating and Downloading Certificate',
        );
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/pdf',
            };
            const payload = {
                user_id: userId,
                course_id: courseId,
            };

            const response = await userAxios.post(
                `/cle/download-certificate`,
                payload,
                {
                    headers,
                    responseType: 'blob', // important for binary data like PDF
                },
            );

            const blob = new Blob([response.data], { type: 'application/pdf' });

            if (launchPdfInModal) {
                // Return the blob URL to be handled by the modal
                const url = window.URL.createObjectURL(blob);
                return { success: true, pdfUrl: url };
            }
            if (isPrint) {
                const url = window.URL.createObjectURL(blob);
                handlePrintModal(url);
            } else {
                const course_name =
                    courses.find((c) => c.id === courseId)?.name || '';

                // Default behavior - download the file
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Toolkit.law_${course_name.replace(
                    /\s+/g,
                    '_',
                )}_certificate_${capitalize(
                    user.given_name.replace(/\s+/g, '_'),
                )}_${capitalize(user.family_name.replace(/\s+/g, '_'))}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                return { success: true };
            }
        } catch (err) {
            console.error('Failed to download certificate:', err);
            return { error: err.message || 'Error downloading certificate' };
        } finally {
            setShowLoader(false);
        }
    };
    const updateCourseProgress = async (courseId, courseItemId, type) => {
        try {
            const lesson = topics.find((c) => c.id === courseItemId);
            if (!lesson) return;

            if (lesson?.is_completed) {
                logger.log('Lesson already completed, skipping API call.');
                return;
            }

            // const updatedLessons = targetCourse.lessons?.map((lesson) =>
            //     lesson.id === courseItemId
            //         ? { ...lesson, is_completed: true }
            //         : lesson,
            // );

            const isLastLesson =
                lesson?.[topics.length - 1]?.id === courseItemId;

            const payload = {
                user_id: userId,
                course_id: courseId,
                course_item_id: courseItemId,
                type: type, // <--- include type here
                ...(isLastLesson && { last_topic: true }),
            };

            const response = await userAxios.post(
                'cle/course-progress',
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            logger.log('Video watched successfully:', response);
            await getLesson(courseItemId);
            await getCourseTopics(courseId);
            await fetchCourses();
        } catch (error) {
            logger.error(
                'Error updating course progress:',
                error.response?.data || error.message,
            );
        }
    };

    const value = {
        courses,
        loading,
        error,
        fetchCourses,
        getCourseTopics,
        setCourses,
        emailCertificate,
        emailedCertificates,
        downloadCertificate,
        updateCourseProgress,
        topics,
        getLesson,
        lesson,
    };

    return (
        <CourseContext.Provider value={value}>
            {children}
        </CourseContext.Provider>
    );
};

export const useCourses = () => {
    const context = useContext(CourseContext);
    if (!context) {
        throw new Error('useCourses must be used within a CourseProvider');
    }
    return context;
};
